/**
 * The roles /careers lists and the rules an intern application has to meet. The page and the Worker both check a form
 * with `checkCareerForm`, so they never disagree. Browser-safe.
 */
import type { CareerForm, Role } from "./types.ts";

export const ROLES: Role[] = [
  {
    slug: "software-engineering-intern",
    title: "Software Engineering Intern",
    summary: "Build Headcount, the app that applies to jobs for you.",
    location: "Remote (US)",
    employmentType: "Intern",
    department: "Engineering",
    pay: "$45/hr",
    hours: "40 hours/week",
    dates: "",
    posted: "2026-10-29",
    salary: { min: 45, max: 45, per: "HOUR" },
    aboutCompany: [
      "Headcount pulls open roles from company career pages, ranks them against each person's profile, and fills in and submits the applications they approve.",
      "Headcount is live at itsheadcount.com with free and paid plans.",
    ],
    aboutRole: [
      "As a Software Engineering Intern, you'll build Headcount itself: the web app, the API behind it, and the engine that fills in job applications. The code you write ships to production.",
      "This is a remote, full-time internship.",
    ],
    work: [
      "Build and ship features across the web app and API",
      "Write API routes on Cloudflare Workers and data models in Supabase Postgres",
      "Improve the engine that fills in and submits applications on company career sites",
      "Fix bugs and write tests for the code you ship",
    ],
    lookingFor: [
      "Currently pursuing a degree in Computer Science or a related field",
      "Experience with TypeScript and React",
      "Comfortable working across frontend and backend code",
    ],
    niceToHave: [
      "Experience with Postgres or another SQL database",
      "Experience with Cloudflare Workers, Supabase, or Bun",
      "Personal projects, open-source contributions, or hackathon experience",
    ],
    compensation: ["$45/hour, 40 hours per week.", "This is a remote position."],
    open: true,
  },
  {
    slug: "growth-intern",
    title: "Growth Intern",
    summary: "Grow Headcount, the app that applies to jobs for you.",
    location: "Remote (US)",
    employmentType: "Intern",
    department: "Growth",
    pay: "$35/hr",
    hours: "40 hours/week",
    dates: "",
    posted: "2026-10-30",
    salary: { min: 35, max: 35, per: "HOUR" },
    aboutCompany: [
      "Headcount pulls open roles from company career pages, ranks them against each person's profile, and fills in and submits the applications they approve.",
      "Headcount is live at itsheadcount.com with free and paid plans.",
    ],
    aboutRole: [
      "As a Growth Intern, you'll help more job seekers find and use Headcount: the channels that bring people in, the pages they land on, and the steps that turn a visitor into a paying user.",
      "This is a remote, full-time internship.",
    ],
    work: [
      "Plan and run campaigns across social media, student communities, and campus groups",
      "Write and test copy for the landing page, emails, and in-app messages",
      "Track signups, activation, and upgrades, and report on what's working",
      "Talk to users to learn how they found Headcount and what keeps them using it",
    ],
    lookingFor: [
      "Currently pursuing a degree in Business, Marketing, Communications, or a related field",
      "Clear, concise writing",
      "Comfortable working with numbers and spreadsheets",
    ],
    niceToHave: [
      "Experience growing an audience, a student organization, or a product of your own",
      "Experience with SQL or an analytics tool",
      "Recent experience searching for internships or jobs",
    ],
    compensation: ["$35/hour, 40 hours per week.", "This is a remote position."],
    open: true,
  },
  {
    slug: "software-engineer",
    title: "Software Engineer",
    summary: "Build Headcount, the app that applies to jobs for you.",
    location: "Remote (US)",
    employmentType: "Full-time",
    department: "Engineering",
    pay: "$100K – $120K",
    hours: "",
    dates: "",
    posted: "2026-10-30",
    salary: { min: 100000, max: 120000, per: "YEAR" },
    aboutCompany: [
      "Headcount pulls open roles from company career pages, ranks them against each person's profile, and fills in and submits the applications they approve.",
      "Headcount is live at itsheadcount.com with free and paid plans.",
    ],
    aboutRole: [
      "As a Software Engineer, you'll build Headcount itself: the web app, the API behind it, and the engine that fills in job applications. You'll own features from design to production.",
      "This is a remote, full-time position.",
    ],
    work: [
      "Build and ship features across the web app and API",
      "Write API routes on Cloudflare Workers and data models in Supabase Postgres",
      "Improve the engine that fills in and submits applications on company career sites",
      "Monitor production, fix bugs, and write tests for the code you ship",
    ],
    lookingFor: [
      "A degree in Computer Science or a related field, or equivalent experience",
      "Professional experience with TypeScript and React",
      "Comfortable working across frontend and backend code",
    ],
    niceToHave: [
      "Experience with Postgres or another SQL database",
      "Experience with Cloudflare Workers, Supabase, or Bun",
      "Experience with browser automation or web scraping",
    ],
    compensation: ["$100,000 – $120,000 per year.", "This is a remote position."],
    open: true,
  },
];

export const roleOf = (slug: string | null | undefined): Role | null => ROLES.find((r) => r.slug === slug) ?? null;

/** Longest value each text field takes. `supabase/migrations/0015_careers.sql` holds the same limits. */
export const CAREER_MAX = {
  fullName: 120,
  phone: 30,
  school: 160,
  link: 300,
  project: 1500,
  resumeName: 255,
  /** The longest address mail can carry (RFC 5321), under the 320 the table allows. */
  email: 254,
} as const;

/** Largest resume the `resumes` bucket takes. */
export const RESUME_BYTES = 10 * 1024 * 1024;

/** What an applicant who answers no to work authorization is told. */
export const NEEDS_US_AUTH = "This role requires authorization to work in the United States.";

export type CareerProblems = Partial<Record<keyof CareerForm, string>>;

/** A link as typed, with https:// added when it has no scheme, or null when it isn't a web address on `host`. */
export function webLink(v: string, host?: string): string | null {
  const raw = v.trim();
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(raw) ? raw : `https://${raw}`);
    if (!/^https?:$/.test(u.protocol) || !u.hostname.includes(".")) return null;
    if (host && u.hostname !== host && !u.hostname.endsWith(`.${host}`)) return null;
    return u.href;
  } catch {
    return null;
  }
}

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH = /^(\d{4})-(0[1-9]|1[0-2])$/;

const realDate = (s: string) => {
  const m = s.match(DATE);
  if (!m) return false;
  const d = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s && +m[1]! >= 2000 && +m[1]! <= 2100;
};

/**
 * A submitted form, trimmed and with links made whole, and what is wrong with it, one plain sentence per field. The form
 * is only good to save when `problems` is empty. Unknown keys are dropped.
 */
export function checkCareerForm(body: Record<string, unknown>): { form: CareerForm; problems: CareerProblems } {
  const problems: CareerProblems = {};
  const text = (k: keyof CareerForm) => (typeof body[k] === "string" ? (body[k] as string).trim() : "");
  const need = (k: keyof CareerForm, max: number, missing: string) => {
    const v = text(k);
    if (!v) problems[k] = missing;
    else if (v.length > max) problems[k] = `Keep it under ${max} characters.`;
    return v;
  };
  const link = (k: "linkedin" | "github" | "website", host: string | undefined, wrong: string, missing?: string) => {
    const v = text(k);
    if (!v) {
      if (missing) problems[k] = missing;
      return "";
    }
    const url = webLink(v, host);
    if (!url || url.length > CAREER_MAX.link) problems[k] = wrong;
    return url ?? v;
  };

  const phone = need("phone", CAREER_MAX.phone, "Enter your phone number.");
  if (phone && !problems.phone && (!/^[0-9+().\s-]+$/.test(phone) || phone.replace(/\D/g, "").length < 7)) problems.phone = "Enter a valid phone number.";
  const graduation = text("graduation");
  if (!MONTH.test(graduation) || +graduation.slice(0, 4) < 2000 || +graduation.slice(0, 4) > 2100) problems.graduation = "Select your graduation month and year.";
  if (typeof body.authorizedUs !== "boolean") problems.authorizedUs = "Select yes or no.";
  else if (!body.authorizedUs) problems.authorizedUs = NEEDS_US_AUTH;
  const startDate = text("startDate");
  if (!realDate(startDate)) problems.startDate = "Enter your earliest start date.";
  const project = text("project");
  if (project.length > CAREER_MAX.project) problems.project = `Keep it under ${CAREER_MAX.project} characters.`;
  const resumeName = need("resumeName", CAREER_MAX.resumeName, "Upload your resume.");
  if (resumeName && !problems.resumeName && !/\.pdf$/i.test(resumeName)) problems.resumeName = "Upload your resume as a PDF.";

  const form: CareerForm = {
    fullName: need("fullName", CAREER_MAX.fullName, "Enter your full name."),
    phone,
    linkedin: link("linkedin", "linkedin.com", "Enter a LinkedIn profile URL.", "Enter your LinkedIn profile URL."),
    github: link("github", "github.com", "Enter a GitHub profile URL."),
    website: link("website", undefined, "Enter a valid URL."),
    school: need("school", CAREER_MAX.school, "Enter your school."),
    graduation,
    authorizedUs: body.authorizedUs === true,
    startDate,
    project,
    resumeName,
    updatesOptIn: body.updatesOptIn === true,
  };
  return { form, problems };
}

/** The email a signed-out applicant types, trimmed, and what is wrong with it, if anything. */
export function checkCareerEmail(v: unknown): { email: string; problem?: string } {
  const email = typeof v === "string" ? v.trim() : "";
  if (!email) return { email, problem: "Enter your email." };
  if (email.length > CAREER_MAX.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return { email, problem: "Enter a valid email address." };
  return { email };
}
